import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useProject } from '../../context/ProjectContext';

/**
 * ConversationSettingsModal - Edit conversation-specific settings (SAT-107)
 *
 * Allows users to set a custom system prompt for a specific conversation.
 * The hierarchy is: Conversation > Project > Global
 *
 * Features:
 * - System prompt editor with character limit
 * - Preview of instruction hierarchy
 * - Clear option to revert to defaults
 * - Save to conversation settings JSON
 */

const MAX_SYSTEM_PROMPT_LENGTH = 4000;

function ConversationSettingsModal({ isOpen, onClose, conversationId, currentSettings, onSave }) {
  const { selectedProject, getProjectCustomInstructions } = useProject();
  const modalRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Local state for editing
  const [systemPrompt, setSystemPrompt] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showHierarchyPreview, setShowHierarchyPreview] = useState(false);

  // Initialize from current settings
  useEffect(() => {
    if (isOpen && currentSettings) {
      setSystemPrompt(currentSettings.systemPrompt || '');
      setHasChanges(false);
    }
  }, [isOpen, currentSettings]);

  // Handle escape key and focus trap
  useEffect(() => {
    if (!isOpen) return;

    previouslyFocusedRef.current = document.activeElement;

    const focusableSelector = 'button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const firstFocusable = modalRef.current?.querySelector(focusableSelector);
    setTimeout(() => firstFocusable?.focus(), 0);

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = Array.from(
          modalRef.current.querySelectorAll(focusableSelector)
        ).filter(el => {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && !el.disabled;
        });

        if (focusableElements.length === 0) {
          e.preventDefault();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedRef.current?.focus();
    };
  }, [isOpen]);

  const handleClose = () => {
    if (hasChanges) {
      // Reset to original
      setSystemPrompt(currentSettings?.systemPrompt || '');
    }
    setHasChanges(false);
    setShowHierarchyPreview(false);
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const newSettings = {
        ...currentSettings,
        systemPrompt: systemPrompt.trim() || null
      };

      await onSave(newSettings);
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Failed to save conversation settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = () => {
    setSystemPrompt('');
    setHasChanges(true);
  };

  const handleSystemPromptChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_SYSTEM_PROMPT_LENGTH) {
      setSystemPrompt(value);
      setHasChanges(true);
    }
  };

  // Get global custom instructions from localStorage
  const getGlobalInstructions = () => {
    try {
      const saved = localStorage.getItem('app-settings');
      if (saved) {
        const settings = JSON.parse(saved);
        return settings.customInstructions || '';
      }
    } catch (e) {
      // Ignore
    }
    return '';
  };

  const globalInstructions = getGlobalInstructions();
  const projectInstructions = getProjectCustomInstructions() || '';

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fadeIn"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="conv-settings-title"
    >
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh]
          flex flex-col animate-fadeInScale"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-decisiv-blue/10 rounded-lg">
              <svg className="w-5 h-5 text-decisiv-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h2 id="conv-settings-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                Conversation Settings
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Customize behavior for this conversation only
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200
              transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* System Prompt Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block font-medium text-gray-900 dark:text-white">
                Custom System Prompt
              </label>
              {systemPrompt && (
                <button
                  onClick={handleClear}
                  className="text-sm text-decisiv-blue hover:text-decisiv-blue/80 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Set custom instructions that override global and project settings for this conversation.
            </p>
            <textarea
              value={systemPrompt}
              onChange={handleSystemPromptChange}
              className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600
                rounded-lg px-4 py-3 text-gray-900 dark:text-white placeholder-gray-500
                resize-none focus:ring-2 focus:ring-decisiv-blue/50 outline-hidden font-mono text-sm"
              rows={8}
              placeholder="Example: You are an expert Python developer. Always use type hints and follow PEP 8 style guide. Explain concepts clearly and provide working code examples..."
            />
            <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
              <span>
                {systemPrompt.length} / {MAX_SYSTEM_PROMPT_LENGTH} characters
              </span>
              <span className={systemPrompt.length > MAX_SYSTEM_PROMPT_LENGTH * 0.9 ? 'text-yellow-500' : ''}>
                {systemPrompt.length > MAX_SYSTEM_PROMPT_LENGTH * 0.9 && 'Approaching limit'}
              </span>
            </div>
          </div>

          {/* Hierarchy Preview Section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowHierarchyPreview(!showHierarchyPreview)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50
                hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Instruction Hierarchy Preview
                </span>
              </div>
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform ${showHierarchyPreview ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showHierarchyPreview && (
              <div className="p-4 space-y-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Instructions are applied in order of priority (highest to lowest):
                </p>

                {/* Conversation Level (Highest Priority) */}
                <div className={`p-3 rounded-lg border-2 ${
                  systemPrompt
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase">
                      1. Conversation
                    </span>
                    {systemPrompt && (
                      <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-sm">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                    {systemPrompt || <span className="italic text-gray-400">Not set</span>}
                  </p>
                </div>

                {/* Project Level */}
                <div className={`p-3 rounded-lg border ${
                  projectInstructions && !systemPrompt
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase">
                      2. Project
                    </span>
                    {selectedProject && (
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-sm">
                        {selectedProject.name}
                      </span>
                    )}
                    {projectInstructions && !systemPrompt && (
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-sm">
                        Active
                      </span>
                    )}
                    {projectInstructions && systemPrompt && (
                      <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-sm">
                        Overridden
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                    {projectInstructions || <span className="italic text-gray-400">Not set</span>}
                  </p>
                </div>

                {/* Global Level */}
                <div className={`p-3 rounded-lg border ${
                  globalInstructions && !systemPrompt && !projectInstructions
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase">
                      3. Global
                    </span>
                    {globalInstructions && !systemPrompt && !projectInstructions && (
                      <span className="text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-sm">
                        Active
                      </span>
                    )}
                    {globalInstructions && (systemPrompt || projectInstructions) && (
                      <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-sm">
                        Overridden
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                    {globalInstructions || <span className="italic text-gray-400">Not set</span>}
                  </p>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                  The highest-priority instruction that is set will be used as the system prompt.
                </p>
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <svg className="w-4 h-4 text-decisiv-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Tips for effective system prompts:
            </h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>- Be specific about your role (e.g., "You are a Service Advisor at a Ford dealership")</li>
              <li>- Specify workflow guidance — run workflows to make your day-to-day job more effective</li>
              <li>- Set the tone or style (e.g., "Be concise and focus on actionable steps")</li>
              <li>- Include any dealership-specific context (e.g., "We specialize in trucks and SUVs")</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {hasChanges && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                Unsaved changes
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600
                rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="px-4 py-2 text-sm font-medium text-white
                bg-decisiv-blue hover:bg-decisiv-blue/90 disabled:opacity-50
                disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
            >
              {isSaving && (
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ConversationSettingsModal;
